import React from "react";

import { useTranslation } from "react-i18next";

import ActionButtons from "../../../../../../components/Generic/ActionButtons";
import InventoryComponent from "../../../../../../components/Generic/QuotedInventory";
import LoadingModal from "../../../../../../components/LoadingModal";
import { ExternalList } from "../../../../../../context/MovingResources";
import { DifferentLocation as LocationAddress } from "../../../../../../models/location";
import { Material } from "../../../../../../models/material";
import { RoomJob, QuotedItem } from "../../../../../../models/sales";
import { convertMetricVolume, handleGetConvertValue } from "../../../../../../util/converts";
import { DESTINATION } from "../../../../../../util/enums";
import { getMeasurement, getWeightMeasurement } from "../../../../../../util/getMeasurement";
import useStyles from "./styles";

enum Attribute {
  VOLUME = "volume",
  WEIGHT = "weight",
}

type ItemJobAttribute = Attribute.VOLUME | Attribute.WEIGHT;

interface InventoryProps {
  inventory?: RoomJob[];
  quoteJobId: number;
  defaultMode: string;
  isOnViewMode?: boolean;
  destinations: LocationAddress[];
  externalList?: ExternalList;
  isUpdating?: boolean;
  isSavingAndSend?: boolean;
  update?: (value: RoomJob[]) => void;
  onSave?: () => void;
  onCancel?: () => void;
  onSaveAndSend?: () => void;
}

const Inventory = ({
  inventory,
  destinations,
  quoteJobId,
  defaultMode,
  externalList,
  isUpdating,
  isOnViewMode = false,
  isSavingAndSend = false,
  update,
  onSave,
  onCancel,
  onSaveAndSend,
}: InventoryProps) => {
  const classes = useStyles(isOnViewMode)();
  const { t: translate } = useTranslation();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const returnTotalOfItem = (
    attribute: ItemJobAttribute,
    item: QuotedItem,
    total: number
  ): number => {
    if (item.itemJob.destination?.name === DESTINATION.NOT_GOING) return total;

    if (attribute === Attribute.VOLUME)
      return (
        total +
        Number(convertMetricVolume(item.itemJob[attribute], measurement?.code, 1)) *
          Number(item.itemJob.quantity)
      );

    if (attribute === Attribute.WEIGHT)
      return (
        total +
        Number(
          handleGetConvertValue(
            item.itemJob[attribute] || 0,
            measurementWeight?.code || "kg",
            measurementWeight?.decimalPlaces
          )
        )
      );

    return total + Number(item.itemJob[attribute]) * Number(item.itemJob.quantity);
  };

  const returnTotalOfMaterial = (
    attribute: ItemJobAttribute,
    material: Material,
    total: number
  ): number => {
    if (material.serviceBooking.isItem && material.destination?.name !== DESTINATION.NOT_GOING) {
      if (material[attribute])
        return total + Number(material[attribute]) * Number(material.quantity);
    }

    return total;
  };

  const handleGetTotalQuantity = (attribute: string): any => {
    let totalOfItems = 0;
    let totalOfMaterials = 0;

    inventory?.forEach((room: RoomJob) => {
      if (attribute === Attribute.VOLUME) {
        totalOfItems += room.itemsJob.reduce(
          (total: number, item: QuotedItem) => returnTotalOfItem(Attribute.VOLUME, item, total),
          0
        );
        totalOfMaterials += room.materials.reduce(
          (total: number, material: Material) =>
            returnTotalOfMaterial(Attribute.VOLUME, material, total),
          0
        );
      } else if (attribute === Attribute.WEIGHT) {
        totalOfItems += room.itemsJob.reduce(
          (total: number, item: QuotedItem) => returnTotalOfItem(Attribute.WEIGHT, item, total),
          0
        );
        totalOfMaterials += room.materials.reduce(
          (total: number, material: Material) =>
            returnTotalOfMaterial(Attribute.WEIGHT, material, total),
          0
        );
      } else {
        totalOfItems += room.itemsJob.reduce((total: number, item: QuotedItem) => {
          if (item.itemJob.destination?.name === DESTINATION.NOT_GOING) return total;

          return total + Number(item.itemJob.quantity);
        }, 0);
        totalOfMaterials += room.materials.reduce((total: number, material: Material) => {
          if (
            material?.serviceBooking?.isItem &&
            material.destination?.name === DESTINATION.NOT_GOING
          )
            return total;

          return total + (material?.serviceBooking?.isItem ? Number(material.quantity) : 0);
        }, 0);
      }
    });

    return Number(totalOfItems + totalOfMaterials);
  };

  return (
    <div>
      <div className={classes.container}>
        <header className={classes.headerWrapper}>
          <span data-cy="page-title">{translate("quotedInventory")}</span>
          <ul>
            <li data-cy="items-quantity">
              <span>{translate("totalQty")}</span>
              <span className="value">{handleGetTotalQuantity("quantity")}</span>
            </li>
            <li data-cy="items-volume">
              <span>{translate("totalVolume")}</span>
              <span className="value">
                {Number(handleGetTotalQuantity("volume"))?.toFixed(2)}
                {measurement?.code}
              </span>
            </li>
            <li data-cy="item-weight">
              <span>{translate("totalWeight")}</span>
              <span className="value">
                {Number(handleGetTotalQuantity("weight"))?.toFixed(2)}
                {measurementWeight?.code || "kg"}
              </span>
            </li>
          </ul>
        </header>
        <InventoryComponent
          inventory={inventory || []}
          defaultMode={defaultMode}
          jobId={quoteJobId}
          locations={destinations}
          update={(value: RoomJob[]) => update && update(value)}
          externalList={externalList}
          viewOnly={isOnViewMode}
          isOnViewMode={isOnViewMode}
        />
        {!isOnViewMode && (
          <div className={classes.actionsButtons}>
            <ActionButtons
              showSaveAndSendButton={isSavingAndSend}
              cancelDisabled={isUpdating}
              saveDisabled={isUpdating}
              onCancel={onCancel}
              onSave={onSave}
              onSaveAndSend={onSaveAndSend}
            />
          </div>
        )}
      </div>
      <LoadingModal showing={isUpdating} />
    </div>
  );
};

export default Inventory;
